import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { ButtonGroup } from "@/uiKit/atoms/ButtonGroup.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { TooltipMenuV2 } from "@/uiKit/atoms/TooltipMenuV2.tsx";
import { PatientRenewalAction } from "@/uiKit/molecules/CarePlan/PatientRenewalAction.tsx";
import { InterruptCarePlanModal } from "@/uiKit/molecules/InterruptCarePlanModal.tsx";
import { PatientRenewal } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientRenewal.tsx";

export type CarePlanManagementActionsProps = {
  patientId: string;
  className?: string;
};

export const CarePlanManagementActions: React.FC<CarePlanManagementActionsProps> = ({ patientId, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const renewalInfo = Queries.practitioner.usePatientRenewInfo(patientId);
  const interruptCarePlan = Queries.practitioner.useInterruptCarePlan();
  const [isRenewalOpen, setIsRenewalOpen] = React.useState(false);
  const [isInterruptionOpen, setIsInterruptionOpen] = React.useState(false);
  const hasAdditionalActions = !!renewalInfo.canInterruptCarePlan;

  return (
    <>
      <ButtonGroup variant="contained" className={cx(styles.container, className)}>
        <PatientRenewalAction renewalInfo={renewalInfo} dataSource="header" onClick={() => setIsRenewalOpen(true)} />
        {hasAdditionalActions && (
          <TooltipMenuV2
            popupId="care-plan-management-menu"
            placement="bottom-end"
            content={[
              <Button
                key="interruption"
                variant="text"
                data-action="care-plan.interruption.open"
                className={styles.interruptionButton}
                disabled={!renewalInfo.canInterruptCarePlan}
                startIcon={<Icons.exit className={styles.icon} />}
                onClick={() => setIsInterruptionOpen(true)}
              >
                {t("carePlanInterruption.action")}
              </Button>,
            ]}
          >
            <Button
              variant="contained"
              data-action="care-plan.management.open"
              startIcon={<Icons.arrowDown className={styles.icon} />}
            />
          </TooltipMenuV2>
        )}
      </ButtonGroup>
      {renewalInfo.renewalType && (
        <PatientRenewal
          patientId={patientId}
          open={isRenewalOpen}
          renewalType={renewalInfo.renewalType}
          onClose={() => setIsRenewalOpen(false)}
          onSubmit={() => setIsRenewalOpen(false)}
        />
      )}
      {renewalInfo.canInterruptCarePlan && (
        <InterruptCarePlanModal
          open={isInterruptionOpen}
          onClose={() => setIsInterruptionOpen(false)}
          onSubmit={reason =>
            interruptCarePlan.mutate(
              { patientId, reason },
              {
                onSuccess: () => {
                  setIsInterruptionOpen(false);
                  TextToast.success(t("carePlanInterruption.successToast"));
                },
              },
            )
          }
          isPending={interruptCarePlan.isPending}
        />
      )}
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
  interruptionButton: css({
    color: `${theme.palette.red.main} !important`,
    "&:disabled": {
      opacity: 0.5,
    },
  }),
});
